import { useState } from 'react';

import type {
  ConnectingLineDelayByStationProperties,
  ConnectingLineEntry,
} from '../../../api/types';
import type { IStationsLine } from '../../../data/istationsLines';
import type { RouteMaster } from '../../../data/routes';
import { getRouteColor } from '../../../data/routes';

import { sortStopsBySequence, type RouteStop } from '../../../data/routeStops';
import {
  CONNECTING_STATION_INTRO_DEFAULT_SSML,
  createDefaultConnectingStationLine,
  isConnectingStationOtherMode,
} from '../../../utils/broadcastContents';

import { PlaceholderLegend } from '../parts/PlaceholderLegend';
import { StopSelect } from '../parts/StopSelect';
import { CONNECTING_STATION_INTRO_PLACEHOLDERS } from '../placeholders';
import { ConnectingLineBlock } from './ConnectingLineBlock';

// ===========================================================================
// 接続線遅延 (乗換駅ベース / part2 / `connecting_line_delay_by_station`) の UI
// ===========================================================================
//
// 三層構造:
//   ConnectingLineDelayByStationFields      … コンテンツ全体 (乗換駅+接続線一覧+冒頭SSML)
//     └── ConnectingLineBlock (接続線1本)   … route_id / 判定モード / 乗換時間 / 方面一覧 / 遅延放送パターン
//            └── ConnectingDirectionBlock   … direction_id / direction_name / 区間 (section時)
//
// 状態管理は上流 (BroadcastContentsEditor) がコントロールドに握るため、各コンポーネントは
// 「現在の値」と「更新関数」を props で受け取り、内部状態は保持しない。

/**
 * 接続線遅延 (乗換駅ベース / part2) のトップレベルフィールド。
 * 乗換駅、冒頭SSML、接続線ブロック一覧を編集する。
 */
export function ConnectingLineDelayByStationFields({
  props,
  routes,
  routeStops,
  istationsLines = [],
  parentRouteId,
  onChange,
}: {
  props: Partial<ConnectingLineDelayByStationProperties>;
  routes: RouteMaster[];
  routeStops: Record<string, RouteStop[]>;
  /**
   * 「運行情報の参照元（アイステイションズ路線）」の選択肢マスタ。
   * 各接続線ブロック (ConnectingLineBlock) にそのまま渡す。
   * ATOS 線区マスタ (`routes`) とは独立した別系統で、連動させない。
   */
  istationsLines?: IStationsLine[];

  /**
   * 親フォーム側で編集中の放送地点の「自路線」の route_id。
   * 乗換駅ドロップダウンの選択肢を、この route_id に対応する駅一覧のみに絞り込むために使う。
   * 詳細は `BroadcastContentsEditorProps.parentRouteId` を参照。
   */
  parentRouteId?: string;
  onChange: (props: ConnectingLineDelayByStationProperties) => void;
}): React.JSX.Element {
  const transferStopId = props.transfer_stop_id ?? '';
  const introTemplate = props.ssml_intro_template ?? CONNECTING_STATION_INTRO_DEFAULT_SSML;
  const lines: ConnectingLineEntry[] = Array.isArray(props.lines) ? props.lines : [];

  // 「その他」モード (乗換駅を使わず、運行情報のみを放送する) かどうか。
  // 判定は utils 側の共通ヘルパに集約し、バリデーション / SSML 補完と規則を揃える。
  const isOtherMode = isConnectingStationOtherMode(props);

  // タブ表示: 選択中の接続線ブロック index。接続線が複数あるとき横並びタブで切り替える。
  const [activeLineIndex, setActiveLineIndex] = useState<number>(0);
  const activeIndex = Math.min(activeLineIndex, Math.max(0, lines.length - 1));

  // 「乗換駅」ドロップダウンの選択肢は、親フォームで選択中の路線 (parentRouteId) の駅一覧を使う。
  //
  // 親から渡される `routeStops` は複数 route_id ぶんが 1 つの Record にまとまっている
  // (BroadcastPointsPage が全路線ぶん事前取得しているため) ので、明示的に
  // `routeStops[parentRouteId]` で自路線に絞り込む必要がある。
  //
  // 以前は `Object.values(routeStops)[0]` で「Record の先頭キー」を暗黙採用していたが、
  // Record の並び順は保証されず、複数 route_id が入っていると自路線とは無関係な路線
  // (例: 中総緩の地点を編集中なのに京浜東北線の駅が候補に出る) の駅一覧が
  // 表示される不具合が発生していたため、`parentRouteId` による明示指定に変更した。
  //
  // `parentRouteId` が未指定 / 該当する駅一覧が routeStops に無い場合は空配列とし、
  // 誤って別路線の駅が表示されないようにする (乗換駅ドロップダウンは
  // 「(指定なし)」のみとなる)。
  const parentStops = sortStopsBySequence(parentRouteId ? (routeStops[parentRouteId] ?? []) : []);

  /** properties 全体を新しい値でまとめて置き換えるヘルパ */
  const emit = (patch: Partial<ConnectingLineDelayByStationProperties>): void => {
    onChange({
      transfer_stop_id: transferStopId,
      ssml_intro_template: introTemplate,
      lines,
      ...props,
      ...patch,
    } as ConnectingLineDelayByStationProperties);
  };

  /**
   * 乗換駅の指定方法 (`transfer_stop_mode`) を切り替える。
   *
   * - `'other'` へ切り替え: 乗換駅・冒頭 SSML は使わないので値を落とす
   *   (DB / JSON にゴミを残さない既存方針を踏襲)。
   * - `'station'` へ戻す: 既定値と同義なのでキーごと削除し、既存 item と
   *   bit-perfect 互換な保存形に戻す。冒頭 SSML はデフォルト文言を復元する。
   */
  const changeTransferStopMode = (nextIsOther: boolean): void => {
    if (nextIsOther) {
      emit({
        transfer_stop_mode: 'other',
        transfer_stop_id: '',
        ssml_intro_template: undefined,
      });
      return;
    }
    emit({
      transfer_stop_mode: undefined,
      ssml_intro_template: CONNECTING_STATION_INTRO_DEFAULT_SSML,
    });
  };

  /** 接続線一覧の index 番目を patch で更新する */

  const updateLine = (index: number, next: ConnectingLineEntry): void => {
    const nextLines = lines.slice();
    nextLines[index] = next;
    emit({ lines: nextLines });
  };

  /**
   * 接続線を index の直後に追加する。
   *
   * 追加した接続線 (= index + 1) をそのまま選択中タブにする。
   * 接続線ブロックはタブ表示で 1 件ぶんしか本文を描画しないため、追加直後に
   * 選択タブを切り替えないと「追加したのに画面が変わらない」状態になり、
   * ユーザが追加した接続線に気付けない (Issue #356)。
   */
  const addLineAfter = (index: number): void => {
    const nextLines = lines.slice();
    nextLines.splice(index + 1, 0, createDefaultConnectingStationLine());
    setActiveLineIndex(index + 1);
    emit({ lines: nextLines });
  };

  /**
   * 接続線を末尾に追加する (ヘッダーの「＋ 接続線を追加」ボタン用)。
   *
   * `addLineAfter` と同様、追加した末尾の接続線を選択中タブにして
   * 追加直後にその内容を編集できるようにする (Issue #356)。
   * 追加後の末尾 index は「追加前の件数」= `lines.length` となる。
   */
  const addLineToEnd = (): void => {
    setActiveLineIndex(lines.length);
    emit({ lines: [...lines, createDefaultConnectingStationLine()] });
  };

  /** 接続線を index から削除する (最後の 1 件は残す) */
  const removeLine = (index: number): void => {
    if (lines.length <= 1) return;
    const nextLines = lines.slice();
    nextLines.splice(index, 1);
    emit({ lines: nextLines });
  };

  /** 接続線を上へ移動 */
  const moveLineUp = (index: number): void => {
    if (index <= 0) return;
    const nextLines = lines.slice();
    const [moved] = nextLines.splice(index, 1);
    if (!moved) return;
    nextLines.splice(index - 1, 0, moved);
    emit({ lines: nextLines });
  };
  /** 接続線を下へ移動 */
  const moveLineDown = (index: number): void => {
    if (index >= lines.length - 1) return;
    const nextLines = lines.slice();
    const [moved] = nextLines.splice(index, 1);
    if (!moved) return;
    nextLines.splice(index + 1, 0, moved);
    emit({ lines: nextLines });
  };

  return (
    <div className="contents-fields">
      {/*
        「乗換駅」と「冒頭 SSML テンプレート」は二段組 (横並び) にして縦スクロールを抑える。

        `.contents-fields` 自体も grid の多段組だが、乗換駅のヒント文 (`<p class="hint">`) は
        CSS ルール `.contents-fields > p { grid-column: 1 / -1 }` で全幅を占有し、
        そこで行が折り返されてしまうため素のままでは縦積みになる。
        そこで 2 項目を `.field-pair-2col` で囲み、ヒント文は左セル (乗換駅) の内側に
        入れることで「必ず横 2 列」を保証する (狭幅では CSS 側で 1 列へフォールバック)。
      */}
      <div className="field-pair-2col" data-testid="connecting-station-header-pair">
        {/*
          左セル: 乗換駅 (必須) + 「その他」トグル + ヒント文。

          乗換駅ドロップダウンの **右隣** に「その他」トグルボタンを置く。押すと
          「その他」モード (乗換駅を使わず運行情報のみを放送) に切り替わり、
          乗換駅ドロップダウン・冒頭 SSML・接続線の遅延判定系項目が非表示になる。
        */}
        <div>
          <label>
            <span className={isOtherMode ? undefined : 'required'}>乗換駅</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {/*
                「その他」モードでは乗換駅を使わないためドロップダウン自体を出さない
                (残しておくと「選べるのに放送に反映されない」誤解を生む)。
              */}
              {isOtherMode ? (
                <span
                  className="hint"
                  data-testid="connecting-station-transfer-stop-other-label"
                  style={{ flex: 1 }}
                >
                  その他 (乗換駅を指定しません)
                </span>
              ) : (
                <StopSelect
                  ariaLabel="乗換駅"
                  testId="connecting-station-transfer-stop-select"
                  value={transferStopId}
                  stops={parentStops}
                  onChange={(stopId) => emit({ transfer_stop_id: stopId })}
                />
              )}
              <button
                type="button"
                aria-label="その他"
                aria-pressed={isOtherMode}
                data-testid="connecting-station-transfer-other-toggle"
                className={isOtherMode ? 'is-active' : undefined}
                onClick={() => changeTransferStopMode(!isOtherMode)}
              >
                その他
              </button>
            </div>
          </label>
          <p className="hint" style={{ margin: '4px 0 0' }}>
            {isOtherMode
              ? '「その他」モードです。乗換駅・遅延判定は使わず、下の接続線ごとに選んだアイステイションズ路線の運行情報のみを放送します (配信が無ければ放送しません)。通常の乗換駅設定に戻すには、もう一度「その他」を押してください。'
              : '自路線で選択した路線の駅から、乗換駅を 1 つ選んでください (例: 「赤羽」)。この駅を主語として、下の接続線ブロックが順に読み上げられます。乗換駅を使わず運行情報だけを放送したい場合は右の「その他」を押してください。'}
          </p>
        </div>

        {/*
          右セル: 冒頭 SSML テンプレート (必須)。
          「その他」モードでは冒頭文を付けずに運行情報の原文のみを読み上げるため非表示。
        */}
        {isOtherMode ? null : (
          <label>
            <span className="required">冒頭 SSML テンプレート</span>
            <input
              type="text"
              aria-label="冒頭 SSML テンプレート"
              data-testid="connecting-station-intro-template"
              required
              value={introTemplate}
              placeholder={CONNECTING_STATION_INTRO_DEFAULT_SSML}
              onChange={(e) => emit({ ssml_intro_template: e.target.value })}
            />
          </label>
        )}
      </div>
      {/* プレースホルダ凡例は冒頭 SSML 用なので「その他」モードでは非表示 */}
      {isOtherMode ? null : (
        <PlaceholderLegend
          testId="connecting-station-intro-hint"
          items={CONNECTING_STATION_INTRO_PLACEHOLDERS}
        />
      )}

      {/* 接続線ブロック一覧 */}
      <div style={{ marginTop: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <strong>接続線 ({lines.length} 件)</strong>
          <button
            type="button"
            data-testid="connecting-station-add-line-button"
            aria-label="接続線を追加"
            onClick={addLineToEnd}
          >
            ＋ 接続線を追加
          </button>
        </div>
        {lines.length === 0 ? (
          <div className="empty-state" style={{ padding: '8px' }}>
            接続線が設定されていません。「＋ 接続線を追加」から作成してください。
          </div>
        ) : null}

        {/*
         * 接続線ブロックはタブ表示で横並びにする。各タブは接続線の路線カラーで着色し、
         * 選択中の 1 ブロックだけ本文 (ConnectingLineBlock) を表示する。
         */}
        {lines.length > 0 ? (
          <>
            <div className="connecting-line-tabs" role="tablist" data-testid="connecting-line-tabs">
              {lines.map((line, i) => {
                const color = getRouteColor(routes, line.route_id ?? '');
                const routeName =
                  routes.find((r) => r.route_id === line.route_id)?.route_name ||
                  line.route_id ||
                  `接続線#${i + 1}`;
                const isActive = i === activeIndex;
                return (
                  <button
                    key={i}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    data-testid={`connecting-line-tab-${i}`}
                    className={`connecting-line-tab${isActive ? ' is-active' : ''}`}
                    style={{
                      // 路線カラーを常に左端のバーで表示し、選択中はタブ全体を路線カラーで塗る。
                      background: isActive ? color.background : '#fff',
                      color: isActive ? color.text : 'var(--color-fg)',
                      borderBottomColor: color.background,
                    }}
                    onClick={() => setActiveLineIndex(i)}
                  >
                    <span
                      className="connecting-line-tab__swatch"
                      style={{ background: color.background }}
                      aria-hidden="true"
                    />
                    #{i + 1} {routeName}
                  </button>
                );
              })}
            </div>
            <ol className="connecting-station-line-list" style={{ listStyle: 'none', padding: 0 }}>
              {lines.map((line, i) =>
                i === activeIndex ? (
                  <li
                    key={i}
                    className="connecting-station-line-item"
                    data-testid={`connecting-station-line-${i}`}
                    style={{
                      border: '1px solid #ddd',
                      borderRadius: 4,
                      padding: '8px 12px',
                      marginTop: 8,
                    }}
                  >
                    <ConnectingLineBlock
                      index={i}
                      line={line}
                      routes={routes}
                      routeStops={routeStops}
                      istationsLines={istationsLines}
                      // 「その他」モードでは遅延判定系の項目 (route_id / 判定モード /
                      // 方面 / 遅延パターン / SSML) を非表示にし、
                      // アイステイションズ路線の選択のみを表示する。
                      isOtherMode={isOtherMode}
                      canRemove={lines.length > 1}
                      isFirst={i === 0}
                      isLast={i === lines.length - 1}
                      onChange={(next) => updateLine(i, next)}
                      // 追加した接続線を選択中タブにする処理は addLineAfter 内で行う
                      // (ヘッダーの「＋ 接続線を追加」と挙動を揃えるため)。
                      onAdd={() => addLineAfter(i)}
                      onRemove={() => {
                        removeLine(i);
                        setActiveLineIndex(Math.max(0, i - 1));
                      }}
                      onMoveUp={() => {
                        moveLineUp(i);
                        setActiveLineIndex(Math.max(0, i - 1));
                      }}
                      onMoveDown={() => {
                        moveLineDown(i);
                        setActiveLineIndex(Math.min(lines.length - 1, i + 1));
                      }}
                    />
                  </li>
                ) : null,
              )}
            </ol>
          </>
        ) : null}
      </div>
    </div>
  );
}
